import { Tabs } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';

import { ModalServiceContext, RicosContext } from './../../ricos-context';
import { logInvariant } from './../../_shared/log-invariant';
import { PmNodeType } from './../../_shared/pm-node-types';
import { useNodeAttrs } from './../../_shared/use-node-attrs';
import {
  Panel,
  PanelHeader,
  useClickOutside,
  usePanelContainerElement,
} from './../../ui-components';

import { IMAGE_EDITOR_COLOR_PICKER_MODAL_ID, IMAGE_SETTINGS_MODAL_ID } from '../consts';
import { SETTINGS_IMAGE_TABS, SETTINGS_MODAL } from '../data-hooks';
import classes from '../statics/styles/settings-modal-scss';
import type { ImagePluginEditorConfig } from '../types';
import { DesignTab } from './design-tab';
import { SettingsTab } from './settings-tab';

export const ImageTabValue = {
  SETTINGS: 'settings',
  DESIGN: 'design',
} as const;

type ImageTab = (typeof ImageTabValue)[keyof typeof ImageTabValue];

interface SettingsProps {
  nodeId: string;
  config: ImagePluginEditorConfig;
  referenceElement: HTMLDivElement | null;
  initialTab?: ImageTab;
}

export const Settings: FC<SettingsProps> = ({
  nodeId,
  config,
  referenceElement,
  initialTab = ImageTabValue.SETTINGS,
}) => {
  const { t } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const containerEl = usePanelContainerElement();
  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, PmNodeType.IMAGE);
  const [activeTab, setActiveTab] = useState(initialTab);

  const isColorPickerModalOpen = () => modalService.isModalOpen(IMAGE_EDITOR_COLOR_PICKER_MODAL_ID);

  const closeModal = () => modalService.closeModal(IMAGE_SETTINGS_MODAL_ID);

  useClickOutside({
    containerRef: { current: containerEl },
    onClickOutside: (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;

      if (referenceElement?.contains(target as Node)) {
        return;
      }

      closeModal();
    },
    disable: isColorPickerModalOpen(),
  });

  if (!pmNodeAttrs) {
    logInvariant(new Error('Image settings: node attrs not found'));
    return null;
  }

  return (
    <Panel
      onCloseButtonClick={() => modalService.closeModal(IMAGE_SETTINGS_MODAL_ID)}
      dataHook={SETTINGS_MODAL}
    >
      <PanelHeader title={t('ImageSettings_Header')} />

      <Tabs
        dataHook={SETTINGS_IMAGE_TABS}
        className={classes.tabs}
        minWidth={0}
        activeId={activeTab}
        onClick={value => setActiveTab(value.id as ImageTab)}
        items={[
          { id: ImageTabValue.SETTINGS, title: t('ImagePlugin_Settings_Tab_Settings') },
          { id: ImageTabValue.DESIGN, title: t('ImagePlugin_Settings_Tab_Design') },
        ]}
      />

      {activeTab === ImageTabValue.SETTINGS && (
        <SettingsTab pmNodeAttrs={pmNodeAttrs} patchPmNodeAttrs={patchPmNodeAttrs} />
      )}

      {activeTab === ImageTabValue.DESIGN && (
        <DesignTab
          pmNodeAttrs={pmNodeAttrs}
          patchPmNodeAttrs={patchPmNodeAttrs}
          nodeId={nodeId}
          config={config}
        />
      )}
    </Panel>
  );
};
